import { fetchMentionUsers, fetchShipmentTeams } from '@/apis/shipment';
import {
  AlertCompletion,
  ChainOfCustodyItem,
  ErrorLike,
  ShipmentTeamsResponse,
} from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import {
  AIEmailIcon,
  COCDismissIcon,
  CoCReplyIcon,
  CrossFillIcon,
  GreenTickIcon,
  StarIcon,
} from '@/assets/icons/icons';
import { truncateByLength, useToken } from '@/common/helper';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { CustomParagraph, CustomShipmentText } from '@/components';
import { useShipmentPermission } from '@/hooks/useShipmentPermission';
import useUnauthorize from '@/hooks/useUnauthorize';
import { Button, Col, Divider, Flex, Tag, Timeline, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileDownload } from '../../../assets/images';
import { MentionRecipient } from '../partials/CoC_CT';
import AlertNestedSmsRow from '../partials/AlertNestedSmsRow';
import AlertNestedCoCNoteRow from '../partials/AlertNestedCoCNoteRow';
import CoCReplies from '../partials/CoCReplies';
import CoCTimelineDot from '../partials/CoCTimelineDot';
import {
  getMergedTimelineItems,
  mergedTimelineItemKey,
} from '../partials/cocTimelineMerge';
import ReplyCommentBox, { ShipmentTeam } from '../partials/ReplyCommentBox';
import TimelineItemExternal from '../partials/TimelineItemExternal';
import styles from './ExternalMapCOC.module.css';
import { SharePageContentProps } from './types';

/** The external view always receives the row-measurement and time-format props. */
type ExternalMapCOCProps = SharePageContentProps &
  Required<
    Pick<
      SharePageContentProps,
      | 'halfHeight'
      | 'showFull'
      | 'setShowFull'
      | 'getFormattedTime'
      | 'fetchSingleShipmentData'
    >
  >;

const ExternalMapCOC: React.FC<ExternalMapCOCProps> = ({
  shipment,
  shipmentId,
  halfHeight,
  setHalfHeightDirect,
  downloadFile,
  showFull,
  setShowFull,
  setHalfHeight,
  getFormattedTime,
  fetchSingleShipmentData,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const canCreateShipmentForSupport = useShipmentPermission();

  const [openReplyCommentInput, setOpenReplyCommentInput] = useState<
    number | string | null
  >(null);

  const setHalfHeightUnified = (id: string | number, height: number) => {
    if (setHalfHeightDirect) return setHalfHeightDirect(id, height);
    if (setHalfHeight) setHalfHeight((prev) => ({ ...prev, [id]: height }));
  };

  const userColorMap = useMemo(() => {
    const map: Record<string, string> = {};

    shipment?.chain_of_custody?.forEach((custody: ChainOfCustodyItem) => {
      const user = custody?.created_by;

      if (user && !map[user]) {
        map[user] = custody?.author_badge_color || '#ccc';
      }
    });

    return map;
  }, [shipment?.chain_of_custody]);

  function getNotificationLabel(type?: string): string {
    switch (type) {
      case 'AI_CALL':
        return 'AI call';
      case 'AI_EMAIL':
        return 'AI email';
      case 'MOBILE':
        return 'SMS';
      case 'EMAIL':
        return 'Email';
      default:
        return type ?? ''; // fallback (optional)
    }
  }

  const [shipmentTeams, setShipmentTeams] = useState<ShipmentTeam[]>([]);
  useEffect(() => {
    if (!shipmentId || !token) {
      return;
    }
    fetchShipmentTeams(token, shipmentId)
      .then((res: unknown) => {
        const response = res as ShipmentTeamsResponse;
        const teams = response?.data?.data?.data?.common_teams ?? [];
        setShipmentTeams(teams);
      })
      .catch((error: unknown) => {
        unauthorize(error as ErrorLike);
      });
  }, [shipmentId, token, unauthorize]);

  const [mentionRecipients, setMentionRecipients] = useState<
    MentionRecipient[]
  >([]);

  useEffect(() => {
    if (!shipmentId || !token) {
      return;
    }
    fetchMentionUsers(token, shipmentId)
      .then((res) => {
        const additionalOptions = res?.data?.data?.data || [];
        const formattedAdditionalOptions = additionalOptions.map((item) => ({
          id: item?.id,
          name: item?.name,
          email: item?.email || '',
          initials: item?.badge,
        }));
        setMentionRecipients(formattedAdditionalOptions);
      })
      .catch((error: unknown) => {
        unauthorize(error as ErrorLike);
      });
  }, [shipmentId, token, unauthorize]);

  const handleSearchMentions = useCallback(
    async (query: string) => {
      if (!token) return [];
      const res = await fetchMentionUsers(token, shipmentId, undefined, query);
      const items = res?.data?.data?.data || [];
      return items.map((item) => ({
        id: item?.id,
        name: item?.name,
        email: item?.email || '',
        initials: item?.badge,
      }));
    },
    [token, shipmentId]
  );

  const handleToggleReply = (id: string | number | undefined) => {
    setOpenReplyCommentInput((prev) => (prev === id ? null : (id ?? null)));
  };

  const chainOfCustody = shipment?.chain_of_custody ?? [];

  return (
    <div>
      {chainOfCustody.length > 0 && (
        <>
          <Flex
            justify='space-between'
            align='center'
            gap={16}
            className='mt-8'
          >
            <CustomShipmentText
              text={themeT('chainOfCustody', theme.themeType)}
              fontSize='14px'
              fontWeight={600}
              lineHeight='28px'
            />
          </Flex>

          <div className='custom-timeline'>
            <Timeline mode='left'>
              {chainOfCustody.map((custody, index) => {
                const isFirst = index === 0;
                const isLast = index === chainOfCustody.length - 1;

                return (
                  <Timeline.Item
                    key={custody.id}
                    className='custom-timeline-head'
                    dot={
                      <CoCTimelineDot
                        custody={custody}
                        halfHeight={halfHeight[custody.id]}
                        userColor={
                          custody.created_by
                            ? userColorMap[custody.created_by]
                            : undefined
                        }
                      />
                    }
                  >
                    {isFirst && (
                      <div
                        className='hide-tail start-tail'
                        // eslint-disable-next-line react/forbid-dom-props -- position comes from the event row height measured at runtime (halfHeight)
                        style={{ height: (halfHeight[custody.id] ?? 0) + 16 }}
                      />
                    )}
                    {isLast && (
                      <div
                        className='hide-tail last-tail'
                        // eslint-disable-next-line react/forbid-dom-props -- position comes from the event row height measured at runtime (halfHeight)
                        style={{
                          height: `calc(100% - ${
                            (halfHeight[custody.id] ?? 0) + 16
                          }px)`,
                        }}
                      />
                    )}

                    {canCreateShipmentForSupport && (
                      <>
                        {custody?.type !== CoCEventType.OneTimeNotification && (
                          <span
                            onClick={() =>
                              setOpenReplyCommentInput(custody?.id)
                            }
                            className='hover-reply-icon'
                          >
                            <CoCReplyIcon />
                          </span>
                        )}
                      </>
                    )}
                    <div onClick={() => handleToggleReply(custody?.id)}>
                      <TimelineItemExternal
                        isSharePage={false}
                        custody={custody}
                        setShowFull={setShowFull}
                        showFull={showFull}
                        getFormattedTime={getFormattedTime}
                        downloadFile={downloadFile}
                        setHalfHeight={setHalfHeightUnified}
                        shipment={shipment}
                        getNotificationLabel={getNotificationLabel}
                        fetchSingleShipmentData={fetchSingleShipmentData}
                      />
                    </div>

                    <Flex wrap>
                      {custody?.attachments?.map((item) => (
                        <Flex
                          vertical
                          key={`${item?.file_url}-${item?.file_name}`}
                        >
                          <Button
                            type='default'
                            className='zl-btn'
                            onClick={() =>
                              downloadFile(item?.file_url, item?.file_name)
                            }
                          >
                            <img
                              src={FileDownload}
                              width={16}
                              alt=''
                              className='zl-invert-on-dark'
                            />
                          </Button>

                          <CustomParagraph
                            text={truncateByLength(item?.file_name, 6)}
                            fontSize='10px'
                            fontWeight={400}
                            lineHeight='16px'
                            color='var(--text-secondary)'
                            marginBottom='0'
                          />
                        </Flex>
                      ))}
                    </Flex>

                    {/* reply comment box : */}
                    {openReplyCommentInput === custody?.id &&
                    custody?.type !== CoCEventType.OneTimeNotification ? (
                      <div>
                        <ReplyCommentBox
                          custody={custody}
                          mentionRecipients={mentionRecipients}
                          setOpenReplyCommentInput={setOpenReplyCommentInput}
                          fetchSingleShipmentData={fetchSingleShipmentData}
                          specificTeams={shipment?.teams}
                          myTeams={shipmentTeams}
                          lastMessage={
                            custody?.replies?.[custody.replies.length - 1] ||
                            null
                          }
                          shipmentId={shipmentId}
                          onSearchMentions={handleSearchMentions}
                        />
                      </div>
                    ) : null}

                    {/* replies divider and count  */}
                    {(custody?.replies?.length ?? 0) > 0 && (
                      <Divider
                        titlePlacement='start'
                        plain
                        orientationMargin={0.1}
                        style={{
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          width: '90%',
                        }}
                      >
                        {custody?.replies?.length}{' '}
                        {(custody?.replies?.length ?? 0) > 1
                          ? 'replies '
                          : 'reply '}
                        {(custody?.hidden_replies_count ?? 0) > 0 &&
                          '(some hidden)'}
                      </Divider>
                    )}

                    {getMergedTimelineItems(custody, {
                      ignoreEmailEntries: true,
                    })?.map?.((item) => {
                      const completionData =
                        item.type === 'completion'
                          ? (item.data as AlertCompletion)
                          : null;
                      const hasDetailContent = Boolean(
                        completionData?.comment ||
                        completionData?.summary ||
                        completionData?.status_info?.description ||
                        completionData?.status_info?.label ||
                        completionData?.has_transcript ||
                        completionData?.ai_type
                      );

                      return (
                        <div
                          key={mergedTimelineItemKey(item)}
                          className={styles.mergedItem}
                        >
                          {item.type === 'reply' ? (
                            <>
                              <CoCReplies
                                custody={{ ...custody, replies: [item.data] }}
                                downloadFile={downloadFile}
                                getFormattedTime={getFormattedTime}
                                showFull={showFull}
                                setShowFull={setShowFull}
                                setOpenReplyCommentInput={handleToggleReply}
                              />
                            </>
                          ) : item.type === 'automation_sms' ? (
                            <AlertNestedSmsRow
                              data={item.data}
                              getFormattedTime={getFormattedTime}
                              showFull={showFull}
                              setShowFull={setShowFull}
                            />
                          ) : item.type === 'automation_coc_note' ? (
                            <AlertNestedCoCNoteRow
                              data={item.data}
                              getFormattedTime={getFormattedTime}
                              showFull={showFull}
                              setShowFull={setShowFull}
                              downloadFile={downloadFile}
                            />
                          ) : (
                            <>
                              <Flex
                                gap={12}
                                align={hasDetailContent ? 'start' : 'center'}
                                style={{
                                  marginTop: '12px',
                                  marginBottom: '12px',
                                }}
                              >
                                <Col
                                  span={1}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'start',
                                    alignItems: 'center',
                                    marginRight: '20px',
                                  }}
                                >
                                  {/* inbound change here icons */}
                                  {item?.data ? (
                                    <div
                                      className={`one-time-action-type-icon`}
                                    >
                                      {item?.data?.status_info?.description ==
                                        'Needs attention' && (
                                        <div className='need-attention-dot'></div>
                                      )}

                                      {item?.data?.status_info?.label?.toLowerCase() ===
                                      'in progress' ? (
                                        <AIEmailIcon />
                                      ) : item?.data?.status === 'Resolved' ? (
                                        <GreenTickIcon />
                                      ) : item?.data?.status ===
                                        'Not Resolved' ? (
                                        <CrossFillIcon />
                                      ) : item?.data?.status_info
                                          ?.description === 'Handed off' ? (
                                        <CrossFillIcon />
                                      ) : (
                                        <COCDismissIcon />
                                      )}
                                    </div>
                                  ) : null}
                                </Col>
                                <Flex
                                  vertical
                                  justify='space-between'
                                  style={{
                                    width: '100%',
                                    height: 'fit-content',
                                  }}
                                >
                                  {/* Added style here */}
                                  <Flex justify='space-between'>
                                    <Tooltip title={item?.data?.title}>
                                      <span>
                                        <CustomParagraph
                                          text={item?.data?.title}
                                          fontSize='12px'
                                          fontWeight={600}
                                          lineHeight='16px'
                                          color='var(--text-primary)'
                                          marginBottom={'4px'}
                                          whiteSpace='pre-line'
                                        />
                                      </span>
                                    </Tooltip>
                                    {item?.data?.timestamp && (
                                      <span
                                        onClick={() =>
                                          setShowFull((prev) => !prev)
                                        }
                                        className={styles.clickable}
                                      >
                                        <CustomParagraph
                                          text={
                                            showFull
                                              ? getFormattedTime(
                                                  item?.data?.timestamp
                                                )?.full
                                              : getFormattedTime(
                                                  item?.data?.timestamp
                                                )?.relative
                                          }
                                          fontSize='12px'
                                          fontWeight={400}
                                          lineHeight='16px'
                                          color='var(--text-light)'
                                          marginBottom='0'
                                          style={{ whiteSpace: 'nowrap' }}
                                        />
                                      </span>
                                    )}
                                  </Flex>
                                  {/* inbound change here  in item?.data tag */}

                                  <Flex justify='space-between' align='center'>
                                    <div className={`w-full ${styles.minW0}`}>
                                      <Flex
                                        gap={8}
                                        align='center'
                                        className='w-full flex-center btw'
                                      >
                                        <Flex vertical gap={2}>
                                          {item?.data?.status_info
                                            ?.description ===
                                          'Needs attention' ? (
                                            <Tag className='one-time-status-tag need-attention'>
                                              Needs attention
                                            </Tag>
                                          ) : item?.data?.status_info?.label?.toLowerCase() ===
                                            'in progress' ? (
                                            <Tag className='one-time-status-tag processing'>
                                              Processing
                                            </Tag>
                                          ) : null}
                                          {(
                                            item?.data?.status_info
                                              ?.description ?? ''
                                          )
                                            .toLowerCase()
                                            .startsWith('dismissed due to') && (
                                            <CustomParagraph
                                              text={
                                                item?.data?.status_info
                                                  ?.description
                                              }
                                              fontSize='12px'
                                              fontWeight={400}
                                              lineHeight='16px'
                                              color='var(--text-light)'
                                              marginBottom={0}
                                            />
                                          )}

                                          <CustomParagraph
                                            text={item?.data?.comment}
                                            fontSize='12px'
                                            fontWeight={400}
                                            lineHeight='16px'
                                            color='var(--text-light)'
                                            whiteSpace='pre-line'
                                            marginBottom={0}
                                          />
                                        </Flex>
                                        {(item?.data?.ai_type === 'call' ||
                                          item?.data?.ai_type === 'email') && (
                                          <Flex
                                            align='center'
                                            justify='center'
                                            style={{
                                              width: '24px',
                                              height: '24px',
                                            }}
                                          >
                                            <StarIcon width={24} height={24} />
                                          </Flex>
                                        )}
                                      </Flex>

                                      {/* {item?.data?.ai_type === 'call' && (
                                  <CustomButton
                                    text='View call transcript'
                                    bgColor='var(--light-gray)'
                                    marginTop='10px'
                                    color='black'
                                    fontSize='12px'
                                    fontWeight='600'
                                    borderRadius='28px'
                                    width='128px'
                                    boxShadow='none'
                                    height='28px'
                                    onClick={() => {
                                      toggleDrawer(
                                        'open',
                                        'callTranscripts',
                                        shipmentId,
                                        item?.data
                                      );
                                    }}
                                  />
                                )} */}
                                      {/* {item?.data?.ai_type === 'email' && (
                                  <CustomButton
                                    text='View email exchange'
                                    bgColor='var(--light-gray)'
                                    color='black'
                                    fontSize='12px'
                                    marginTop='10px'
                                    fontWeight='600'
                                    borderRadius='28px'
                                    width='139px'
                                    boxShadow='none'
                                    height='28px'
                                    onClick={() => {
                                      toggleDrawer(
                                        'open',
                                        'emailTranscripts',
                                        shipmentId,
                                        item?.data
                                      );
                                    }}
                                  />
                                )} */}
                                    </div>
                                  </Flex>
                                </Flex>
                              </Flex>
                            </>
                          )}
                        </div>
                      );
                    })}

                    {/* {(custody?.replies?.length ?? 0) > 0 && (
                      <>
                        {custody?.replies?.length > 0 && (
                          <Divider
                            titlePlacement='start'
                            plain
                            orientationMargin={0.1}
                            style={{
                              fontSize: '12px',
                              color: 'var(--text-secondary)',
                              width: '90%',
                            }}
                          >
                            {custody?.replies?.length}{' '}
                            {custody?.replies?.length > 1
                              ? 'replies '
                              : 'reply '}
                            {custody?.hidden_replies_count > 0 &&
                              '(some hidden)'}
                          </Divider>
                        )}
                        {custody?.replies?.map((item) => (
                          <CoCReplies
                            key={item?.id}
                            custody={{ ...custody, replies: [item] }}
                            downloadFile={downloadFile}
                            getFormattedTime={getFormattedTime}
                            showFull={showFull}
                            setShowFull={setShowFull}
                          />
                        ))}
                      </>
                    )} */}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </div>
        </>
      )}
    </div>
  );
};

export default ExternalMapCOC;
